import React, { useState, useRef, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Clock } from 'lucide-react';
import { COLORS } from '../utils/colors';
import { SearchContext } from '../App';
import { getTrendUrl } from '../utils/slugs';

const SearchBar = ({ className = "", onFocus, onBlur, hideResults, fullWidth = false }) => {
  const { searchQuery, setSearchQuery, searchResults, allTrends } = useContext(SearchContext);
  const [focused, setFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const navigate = useNavigate();
  
  // Load recent searches from localStorage on component mount
  useEffect(() => {
    const storedSearches = localStorage.getItem('recentSearches');
    if (storedSearches) {
      setRecentSearches(JSON.parse(storedSearches));
    }
  }, []);
  
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setFocused(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleSearch = (e) => {
    e?.preventDefault();
    
    // Don't search if query is empty
    if (!searchQuery?.trim()) return;
    
    // Add to recent searches
    if (searchQuery?.trim() !== '') {
      const updatedSearches = [
        searchQuery,
        ...recentSearches.filter(s => s !== searchQuery)
      ].slice(0, 5); // Keep only 5 most recent
      
      setRecentSearches(updatedSearches);
      localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
    }
    
    // Keep focus state for small screens
    if (window.innerWidth < 768) {
      setFocused(false);
    }
  };
  
  const clearSearch = () => {
    setSearchQuery('');
    inputRef.current?.focus();
  };
  
  const handleSelectSearchResult = (trend) => {
    setFocused(false);
    navigate(getTrendUrl(trend));
  };
  
  const handleInputFocus = () => {
    setFocused(true);
    if (onFocus) onFocus();
  };
  
  const handleInputBlur = () => {
    // Don't immediately blur so click events on results can fire
    setTimeout(() => {
      if (onBlur) onBlur();
    }, 200);
  };
  
  return (
    <div ref={searchContainerRef} className={`relative w-full ${className}`}>
      <form onSubmit={handleSearch} className="relative">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search innovation trends, technologies, methodologies..."
          className={`w-full pl-10 pr-16 py-3 rounded-xl border ${
            focused ? "border-red-300 ring-4 ring-red-100" : "border-gray-200"
          } bg-white/95 backdrop-blur-sm shadow-sm focus:outline-none transition-all duration-300`}
          value={searchQuery || ""}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          aria-label="Search innovation trends"
        />
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          <Search className="w-5 h-5" />
        </div>
        
        {searchQuery && (
          <button
            type="button"
            className="absolute right-16 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
            onClick={clearSearch}
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        
        <button
          type="submit"
          className="absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1.5 text-white rounded-lg hover:bg-red-700 transition-colors"
          style={{ backgroundColor: COLORS.primary }}
          aria-label="Submit search"
        >
          <span className="sr-only sm:not-sr-only">Search</span>
          <Search className="w-4 h-4 sm:hidden" />
        </button>
      </form>
      
      {/* Search results dropdown */}
      {focused && !hideResults && (
        <div className="absolute z-50 top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 p-3 max-h-[70vh] overflow-y-auto">
          {/* Search results */}
          {searchQuery?.trim() !== '' && searchResults?.length > 0 && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Search Results</div>
              <div className="space-y-2">
                {searchResults.slice(0, 4).map((trend) => (
                  <div 
                    key={trend.id}
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                    onClick={() => handleSelectSearchResult(trend)}
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" 
                      style={{ backgroundColor: `${COLORS.primary}15` }}>
                      <span className="text-sm font-medium" style={{ color: COLORS.primary }}>
                        {trend.category.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{trend.title}</div>
                      <div className="text-xs text-gray-500">{trend.category} • {trend.timeHorizon}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </div>
                ))}
                
                {searchResults.length > 4 && (
                  <div className="text-center pt-2">
                    <button 
                      className="text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                      style={{ color: COLORS.primary }}
                      onClick={() => {
                        handleSearch();
                        setFocused(false);
                      }}
                    >
                      See all {searchResults.length} results
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* No results state */}
          {searchQuery?.trim() !== '' && searchResults?.length === 0 && (
            <div className="py-4 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <Search className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-gray-700 font-medium mb-1">No results found</p>
              <p className="text-gray-500 text-sm mb-3">Try different keywords or browse all trends</p>
              <button
                className="px-4 py-2 text-white rounded-lg text-sm font-medium"
                style={{ backgroundColor: COLORS.primary }}
                onClick={() => {
                  setSearchQuery('');
                  setFocused(false);
                }}
              >
                Browse All Trends
              </button>
            </div>
          )}
          
          {/* Recent searches */}
          {(!searchQuery || searchQuery.trim() === '') && recentSearches.length > 0 && (
            <div>
              <div className="text-sm text-gray-500 mb-2">Recent Searches:</div>
              <div className="space-y-1">
                {recentSearches.map((search, i) => (
                  <div 
                    key={i}
                    className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                    onClick={() => {
                      setSearchQuery(search);
                      handleSearch();
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">{search}</span>
                    </div>
                    <button
                      className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        const updatedSearches = recentSearches.filter(s => s !== search);
                        setRecentSearches(updatedSearches);
                        localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
                      }}
                      aria-label={`Remove ${search} from recent searches`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Suggested searches */}
          {(!searchQuery || searchQuery.trim() === '') && (
            <div className={recentSearches.length > 0 ? "mt-4 pt-4 border-t border-gray-200" : ""}>
              <div className="text-sm text-gray-500 mb-2">Suggested searches:</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {["Digital Twins", "Embedded Wallets", "Pay-as-You-Go", "Carbon-Aware Computing", "Voice Commerce", "Dynamic Pricing"].map((suggestion, i) => (
                  <div 
                    key={i}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                    onClick={() => {
                      setSearchQuery(suggestion);
                      handleSearch();
                      setFocused(false);
                    }}
                  >
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{suggestion}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;